import { cn } from './cn.js';

/** Modern scrollable table frame with card elevation. */
export function TableShell({
  children,
  className,
  plain = false,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly plain?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'overflow-x-auto',
        !plain && 'rounded-card border border-border bg-card shadow-card',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Table({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return <table className="w-full border-collapse text-sm">{children}</table>;
}

export function THead({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}): React.ReactElement {
  return (
    <thead className={cn('bg-cream/90 border-b border-border sticky top-0 z-10 backdrop-blur-xs', className)}>
      {children}
    </thead>
  );
}

export function TBody({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return <tbody className="divide-y divide-border">{children}</tbody>;
}

export function Tr({
  children,
  className,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly onClick?: () => void;
}): React.ReactElement {
  return (
    <tr onClick={onClick} className={cn('transition-colors hover:bg-cream/40', className)}>
      {children}
    </tr>
  );
}

export function Th({
  children,
  align = 'left',
  className,
}: {
  readonly children?: React.ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly className?: string;
}): React.ReactElement {
  return (
    <th
      scope="col"
      className={cn(
        'eyebrow px-4 py-3 text-left font-semibold text-ink-faint',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  colSpan,
  className,
}: {
  readonly children?: React.ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly colSpan?: number;
  readonly className?: string;
}): React.ReactElement {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        'px-4 py-3 align-middle text-sm text-ink',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}
